import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Sparkles, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const SWAP_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 10];

const AdminAISettings = () => {
  const [swapsPerEmployee, setSwapsPerEmployee] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/ai-polish-config`);
        if (!cancelled) setSwapsPerEmployee(r.data.swaps_per_employee || 3);
      } catch {
        if (!cancelled) toast.error("Failed to load AI settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/ai-polish-config`, { swaps_per_employee: swapsPerEmployee });
      setSwapsPerEmployee(r.data.swaps_per_employee);
      toast.success("AI suggestion cap updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-ai-settings">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#002FA7]" /> AI Settings
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Tune the AI roster-polish behaviour. Changes apply on the next roster generation.
        </p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded p-6 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-wide text-zinc-400">
            AI suggestion cap (per employee)
          </label>
          <p className="text-xs text-zinc-500 mt-1 mb-3">
            Maximum number of shift swaps the AI may propose for each employee during the polish step.
            Effective cap is <span className="font-mono text-zinc-300">N × employees</span>.
          </p>

          {loading ? (
            <div className="h-10 bg-black/40 border border-zinc-700 rounded animate-pulse" data-testid="ai-cap-loading" />
          ) : (
            <select
              value={swapsPerEmployee}
              onChange={(e) => setSwapsPerEmployee(parseInt(e.target.value, 10))}
              className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100 focus:border-[#002FA7] focus:outline-none"
              data-testid="ai-swaps-per-employee-select"
            >
              {SWAP_OPTIONS.map((n) => (
                <option key={n} value={n}>{n} per employee</option>
              ))}
            </select>
          )}
        </div>

        <button
          onClick={handleSave}
          disabled={saving || loading}
          className="px-4 py-2 bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="ai-settings-save-btn"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save changes</>}
        </button>

        <div className="border-t border-zinc-800 pt-4">
          <p className="text-xs text-zinc-500">
            <strong className="text-zinc-300">Tip:</strong> Increase this if the AI isn't applying enough fairness or preference fixes;
            decrease it if generation is taking too long or making too many changes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminAISettings;
